'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePathname } from 'next/navigation';

export type AuthTransitionPhase =
  | 'idle'
  | 'collapsing'
  | 'exiting'
  | 'navigating'
  | 'logout-exiting'
  | 'logout-mounting'
  | 'logout-entering-form'
  | 'logout-entering-visual';

interface AuthTransitionContextValue {
  phase: AuthTransitionPhase;
  playLogin: (navigate: () => Promise<unknown>) => Promise<void>;
  playLogout: (
    logoutApi: () => Promise<unknown>,
    clearLocal: () => void,
    navigate: () => Promise<unknown>,
  ) => Promise<void>;
}

const AuthTransitionContext = createContext<AuthTransitionContextValue | null>(null);

/**
 * Ceiling on how long the login animation will wait for the router. A navigation
 * that never commits (an error boundary, a dropped request) must not strand the
 * user on a collapsed, form-less login screen forever.
 */
export const LOGIN_NAVIGATION_TIMEOUT_MS = 15_000;

function wait(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function AuthTransitionProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<AuthTransitionPhase>('idle');
  const phaseRef = useRef<AuthTransitionPhase>('idle');

  // The pathname is the only honest signal that a navigation has committed:
  // `router.push` is fire-and-forget (`AppRouterInstance.push` returns void), so
  // it resolves while the RSC round trip is still in flight. An ancestor
  // (`LaunchDarklyGateActive`) already reads the pathname, so this costs no extra
  // render, and the context value below is keyed off `phase` rather than the
  // pathname so consumers don't re-render on every navigation.
  const pathname = usePathname();
  const pathnameRef = useRef<string | null>(pathname);
  const routeWaitersRef = useRef(new Set<() => void>());

  useEffect(() => {
    pathnameRef.current = pathname;
    for (const notify of routeWaitersRef.current) notify();
  }, [pathname]);

  const setPhaseState = (next: AuthTransitionPhase) => {
    phaseRef.current = next;
    setPhase(next);
  };

  /** Resolves once the committed route differs from `from`, or the ceiling hits. */
  const waitForRouteChange = useCallback((from: string | null, timeoutMs: number) => {
    return new Promise<void>((resolve) => {
      if (pathnameRef.current !== from) {
        resolve();
        return;
      }
      const settle = () => {
        routeWaitersRef.current.delete(check);
        clearTimeout(timer);
        resolve();
      };
      const check = () => {
        if (pathnameRef.current !== from) settle();
      };
      const timer = setTimeout(settle, timeoutMs);
      routeWaitersRef.current.add(check);
    });
  }, []);

  const playLogin = useCallback(
    async (navigate: () => Promise<unknown>) => {
      if (phaseRef.current !== 'idle') return;

      const from = pathnameRef.current;

      setPhaseState('collapsing');
      await wait(500);

      setPhaseState('exiting');
      await wait(500);

      setPhaseState('navigating');
      try {
        await navigate();
        // `navigate()` has only *started* the navigation. Hold `navigating` until
        // the route actually commits — returning to `idle` on a fixed timer
        // un-collapses the still-mounted auth layout and repaints the login form
        // the user just submitted, which is what a remote (non-localhost) API
        // makes visible for seconds.
        await waitForRouteChange(from, LOGIN_NAVIGATION_TIMEOUT_MS);
        // Beat after the commit so the main layout's enter animation, which is
        // keyed off `navigating`, gets to play.
        await wait(1000);
      } finally {
        setPhaseState('idle');
      }
    },
    [waitForRouteChange],
  );

  const playLogout = useCallback(
    async (
      logoutApi: () => Promise<unknown>,
      clearLocal: () => void,
      navigate: () => Promise<unknown>,
    ) => {
      if (phaseRef.current !== 'idle') return;

      setPhaseState('logout-exiting');
      logoutApi();
      await wait(820);

      clearLocal();

      setPhaseState('logout-mounting');
      await navigate();
      await wait(50);

      setPhaseState('logout-entering-form');
      await wait(520);

      setPhaseState('logout-entering-visual');
      await wait(620);

      setPhaseState('idle');
    },
    [],
  );

  const value = useMemo(
    () => ({
      phase,
      playLogin,
      playLogout,
    }),
    [phase, playLogin, playLogout],
  );

  return <AuthTransitionContext.Provider value={value}>{children}</AuthTransitionContext.Provider>;
}

export function useAuthTransition() {
  const ctx = useContext(AuthTransitionContext);
  if (!ctx) {
    throw new Error('useAuthTransition must be used inside <AuthTransitionProvider>');
  }
  return ctx;
}
